import React from 'react';
import { NextPage } from 'next';
import {
  AutoCompleteV2, ICON_MAGNIFY,
} from '@doveit/lego';
import { capitalizeWords } from '@doveit/hammer';
import slugify from 'slugify';
import AppLayout from '../client/layouts/app-layout/AppLayout';
import { requireFromStaticAsset } from '../client/utils/assetsUtils';
import {
  buildAgenciesURLObject,
  buildAgencyByLocalityURLObject, goToPage, relativeToAbsoluteURL,
} from '../client/utils/links';
import PageLayout from '../client/layouts/page-layout/PageLayout';
import PageHead, { PageHeadProps } from '../client/components/page-head/PageHead';
import PageSection from '../client/components/page-section/PageSection';
import { City, HeroSection } from '../client/types';
import * as styles from '../client/templates/agencies/Agencies.style';
import * as domain from '../domain/graphcms';
import { withError, withGetServerSideError } from '../client/hocs/with-server-side-error/withServerSideError';
import Hero from '../client/templates/common/hero/Hero';
import initGraphQLClient from '../client/graphql/initGraphQLClient';
import { AGENCIES } from '../client/graphql/queries/agencies';
import {
  AgenciesSection, AgenciesSectionProps, GenericSection, GenericSectionProps, StripesSection, StripesSectionProps,
} from '../client/sections';
import BannerSection, { BannerSectionProps } from '../client/sections/banner-section/BannerSection';

export interface AgenciesProps {
  SEO: PageHeadProps['SEO'],
  hero: HeroSection, // @TODO: use correct prop types
  introSection: GenericSectionProps,
  sellPropertySection: BannerSectionProps,
  cities: City[],
  agenciesSection: AgenciesSectionProps,
  joinUsSection: StripesSectionProps,
}

export const Agencies: NextPage<AgenciesProps> = ({
  SEO,
  hero,
  introSection,
  sellPropertySection,
  cities,
  agenciesSection,
  joinUsSection,
}) => {
  const breadcrumb = React.useMemo(() => ([{
    label: 'Agenzie immobiliari',
  }]), []);

  const searchCity = React.useCallback(
    (query: string) => Promise.resolve(cities.filter((city) => slugify(city.title.toLowerCase()).startsWith(slugify(query.toLowerCase())))),
    [cities],
  );

  const printSuggestion = React.useCallback((city: City) => `Provincia di ${capitalizeWords(city.locality)}`, []);

  const onProvinceSelected = React.useCallback((city?: City) => {
    if (city) {
      goToPage(buildAgencyByLocalityURLObject({ locality: city.locality }));
    }
  }, []);

  return (
    <AppLayout>
      <PageHead SEO={SEO} />
      <PageLayout>
        <Hero
          title={hero.title}
          description={hero.description}
          backgroundImage={hero.backgroundImage}
          primarySlot={(
            <div css={styles.searchAgency}>
              <AutoCompleteV2
                suggestions={searchCity}
                printSuggestion={printSuggestion}
                icon={ICON_MAGNIFY}
                onSuggestionSelected={onProvinceSelected}
                placeholder="Es. Roma"
              />
            </div>
          )}
        />
        <PageLayout.Content>
          <PageLayout.Main>
            <GenericSection {...introSection} />

            <BannerSection {...sellPropertySection} />

            <AgenciesSection {...agenciesSection} />

            {joinUsSection && (
              <StripesSection {...joinUsSection} />
            )}
          </PageLayout.Main>
        </PageLayout.Content>
        <PageSection.Wrapper>
          <PageLayout.Breadcrumb items={breadcrumb} />
        </PageSection.Wrapper>
      </PageLayout>
    </AppLayout>
  );
};

export const getServerSideProps = withGetServerSideError<AgenciesProps>(async () => {
  const graphQLClient = initGraphQLClient();

  const agencyListings = await graphQLClient.query<{
    agencyListings: domain.AgencyListing[],
  }>({ query: AGENCIES });

  const {
    seo, hero, introSection, sellPropertyBannerSection, agenciesSection, joinUsSection,
  } = agencyListings.data.agencyListings[0];
  const agencyListingURLObject = buildAgenciesURLObject();
  const agencies = agenciesSection.agencies
    .sort((a, b) => a.locality.name.localeCompare(b.locality.name));

  return {
    props: {
      SEO: {
        ...seo,
        canonical: relativeToAbsoluteURL(agencyListingURLObject.as),
      },
      hero: {
        title: hero.title.markdown.trim(),
        description: hero.content.markdown.trim(),
        backgroundImage: {
          SMALL: hero.mobileImage?.url || null,
          LARGE: hero.desktopImage?.url || null,
        },
        imageAlt: hero.imageAlt || null,
      },
      introSection: {
        title: introSection.title?.markdown || null,
        content: introSection.content.markdown,
      },
      sellPropertySection: {
        title: sellPropertyBannerSection.title,
        subtitle: sellPropertyBannerSection.subtitle || null,
        content: sellPropertyBannerSection.content.markdown,
        image: {
          url: sellPropertyBannerSection.image.url,
          alt: sellPropertyBannerSection.imageAlt,
        },
        action: sellPropertyBannerSection.cta,
      },
      cities: agencies.map((agency) => ({
        title: agency.locality.name,
        picture: agency.locality.coverPreviewVertical?.url || requireFromStaticAsset('cities/default-city.png'),
        smallPicture: agency.locality.coverPreviewHorizontal?.url || requireFromStaticAsset('cities/small/default-city.png'),
        locality: agency.locality.slug,
      })),
      agenciesSection: {
        title: agenciesSection.title?.markdown || null,
        content: agenciesSection.content?.markdown || null,
        agencies: agencies
          .map(({
            locality,
          }) => ({
            name: locality.name,
            picture: locality.coverPreviewHorizontal?.url || requireFromStaticAsset('cities/small/default-city.png'),
            title: `Agenzia immobiliare ${locality.name}`,
            urlObject: buildAgencyByLocalityURLObject({ locality: locality.slug }),
            overriddenAlt: `Agenzia immobiliare a ${locality.name}`,
          })),
      },
      joinUsSection: joinUsSection.items.length > 0 ? {
        title: joinUsSection.title?.markdown || null,
        content: joinUsSection.content?.markdown || null,
        items: joinUsSection.items.map((item) => ({
          title: item.title,
          subtitle: item.subtitle,
          content: item.content,
          image: {
            url: item.image.url,
            alt: item.imageAlt,
          },
          action: item.action,
          isAbsoluteImageUrl: true,
        })),
      } : null,
    },
  };
});

export default withError(Agencies);
